import { useId, type ComponentPropsWithRef, type KeyboardEvent, type ReactNode } from 'react';
import { activateOnEnter } from '@src/components/activateOnEnter';
import { testIdAttr, testIdFor } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import './Radio.css';

/**
 * A shared `name` makes the browser treat the set as one tab stop — Tab lands
 * on the checked one and skips the rest. We keep `name` for the form value
 * (a hidden input) and move between options ourselves with Tab and arrows.
 */
function moveToSiblingRadio(event: KeyboardEvent<HTMLInputElement>, group: string) {
  const nextKeys = event.key === 'ArrowDown' || event.key === 'ArrowRight';
  const prevKeys = event.key === 'ArrowUp' || event.key === 'ArrowLeft';
  if (!nextKeys && !prevKeys) {
    return;
  }

  const radios = [
    ...document.querySelectorAll<HTMLInputElement>(
      `input[data-radio-group="${CSS.escape(group)}"]`,
    ),
  ].filter((radio) => !radio.disabled);
  const current = radios.indexOf(event.currentTarget);
  if (current < 0 || radios.length < 2) {
    return;
  }

  event.preventDefault();
  const sibling =
    radios[
      nextKeys ? (current + 1) % radios.length : (current - 1 + radios.length) % radios.length
    ];
  sibling.focus();
  sibling.click();
}

type NativeRadioProps = Omit<
  ComponentPropsWithRef<'input'>,
  'style' | 'type' | 'checked' | 'onChange' | 'children'
>;

export interface RadioProps extends NativeRadioProps {
  /** Controlled. In a group, exactly one radio is checked. */
  checked: boolean;
  /** State-first: callers want to know what it became, not the event. */
  onCheckedChange: (checked: boolean) => void;
  /**
   * Sits beside the mark, and is part of the click target. A node rather than a
   * string, because a choice can contain a link or an emphasis.
   */
  label?: ReactNode;
  /** Quiet note under the row, aligned with the label. */
  hint?: string;
  /** Turns the mark red and replaces the hint with the message. */
  error?: string;
  disabled?: boolean;
  /**
   * Only the input and the drawn mark — for embedding in a larger label
   * (Choice). The caller owns the name of the option.
   */
  indicator?: boolean;
  /** Parent id. The input, label and message hang off it. */
  testId?: string;
}

export function Radio({
  checked,
  onCheckedChange,
  label,
  hint,
  error,
  disabled = false,
  indicator = false,
  className,
  id,
  name,
  value,
  onKeyDown,
  testId,
  ...rest
}: RadioProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const messageId = `${inputId}-message`;

  const classes = ['radio'];
  if (indicator) {
    classes.push('radio--indicator');
  }
  if (error) {
    classes.push('radio--invalid');
  }
  if (disabled) {
    classes.push('radio--disabled');
  }
  if (className) {
    classes.push(className);
  }

  // An error is the more urgent of the two, and stacking both under a one-line
  // control makes the row taller than the thing it describes.
  const message = error ?? hint;

  const input = (
    <input
      className="radio__input"
      id={inputId}
      type="radio"
      value={value}
      checked={checked}
      disabled={disabled}
      data-radio-group={name}
      aria-invalid={Boolean(error) || undefined}
      aria-describedby={!indicator && message ? messageId : undefined}
      onChange={(event) => {
        if (event.target.checked) {
          onCheckedChange(true);
        }
      }}
      onClick={() => {
        // A checked radio does not fire `change`. Clicking it again is still a pick.
        if (checked && !disabled) {
          onCheckedChange(true);
        }
      }}
      onKeyDown={(event) => {
        activateOnEnter(event);
        if (name) {
          moveToSiblingRadio(event, String(name));
        }
        onKeyDown?.(event);
      }}
      {...testIdAttr(testIdFor(testId, 'control'))}
      {...rest}
    />
  );

  const mark = (
    <span className="radio__mark" aria-hidden="true">
      <span className="radio__dot" />
    </span>
  );

  if (indicator) {
    return (
      <span className={classes.join(' ')} {...testIdAttr(testId)}>
        <span className="radio__row">
          {input}
          {mark}
        </span>
      </span>
    );
  }

  return (
    <div className={classes.join(' ')} {...testIdAttr(testId)}>
      <label className="radio__row">
        {input}
        {mark}
        {label && (
          <Typography
            as="span"
            role="label/control"
            className="radio__label"
            {...testIdAttr(testIdFor(testId, 'label'))}
          >
            {label}
          </Typography>
        )}
      </label>
      {name != null && name !== '' && checked && <input type="hidden" name={name} value={value} />}

      {message && (
        <Typography
          as="p"
          role={error ? 'meta/error' : 'meta/helper'}
          className="radio__message"
          id={messageId}
          {...testIdAttr(testIdFor(testId, error ? 'error' : 'hint'))}
        >
          {message}
        </Typography>
      )}
    </div>
  );
}
